import { api, apiConfig } from "../components/api/api";

/**
 * «Entrar a mi tienda» desde una tienda SpectrePOS (Chentro/HBS).
 *
 * La tienda no tiene login propio: su /login manda aquí, a
 * `/open-store?next=/ruta`. Si hay sesión de Spectre, se pide un enlace de SSO
 * de un solo uso para el usuario de esa sesión y se vuelve a la tienda. Si no,
 * el router manda a /login como siempre y, al entrar, se retoma el pedido.
 *
 * El pedido se guarda al cargar (antes de que el router lo convierta en
 * /login y se pierda la ruta) y vence a los 15 minutos.
 */
const KEY = "spectre:openStore";
const EMAIL_KEY = "spectre:loginEmail";
const TTL_MS = 15 * 60 * 1000;

/**
 * El correo con el que se está intentando entrar, para que el formulario no lo
 * pierda. Lo trae la tienda en `login_hint` (el dueño ya lo escribió allá) o
 * se va guardando mientras se teclea: el formulario se vuelve a montar cuando
 * la clave falla, y sin esto volvía vacío.
 *
 * Vive en sessionStorage: es de esta pestaña y se va al cerrarla.
 */
export function loginEmailHint() {
  try {
    return sessionStorage.getItem(EMAIL_KEY) || "";
  } catch (_e) {
    return "";
  }
}

export function rememberLoginEmail(email) {
  try {
    sessionStorage.setItem(EMAIL_KEY, String(email || "").trim());
  } catch (_e) {
    // Sin sessionStorage el formulario funciona igual; sólo no recuerda.
  }
}

/** ¿Hay un pedido de «entrar a mi tienda» esperando a que haya sesión? */
export function hasPendingOpenStore() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(KEY) || "null");
    return Boolean(pending) && Date.now() - pending.at <= TTL_MS;
  } catch (_e) {
    return false;
  }
}

export function captureOpenStoreRequest() {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== "/open-store") return;
  const params = new URLSearchParams(window.location.search);
  const next = params.get("next") || "/";
  const hint = params.get("login_hint");
  if (hint) rememberLoginEmail(hint);
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ next, at: Date.now() }));
  } catch (_e) {
    // Sin sessionStorage no hay cómo retomar tras el login; se sigue igual.
  }
  // Que el router nunca vea /open-store (no es una pantalla de v3).
  window.history.replaceState(null, "", "/");
}

/** Con sesión: si hay un pedido pendiente, redirige a la tienda. */
export async function consumeOpenStoreRequest() {
  let pending = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(KEY) || "null");
    sessionStorage.removeItem(KEY);
  } catch (_e) {
    return false;
  }
  if (!pending || Date.now() - pending.at > TTL_MS) return false;
  try {
    const res = await api.ajaxSession("getSpectreposSsoUrl", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: pending.next ? { next: pending.next } : {},
    });
    if (res && res.url) {
      window.location.replace(res.url);
      return true;
    }
  } catch (e) {
    console.warn("open-store: could not get the store link", e);
  }
  return false;
}
